import { RowBetween } from 'components/Layout/Row'
import { Path } from 'config/constants/paths'
import { useTranslation } from 'contexts/Localization'
import { useHistory } from 'react-router-dom'
import {
  Text,
  Box,
  Grid,
  Flex,
  IconButton,
  LinkButton,
  HelpWithQuestionmark,
  Img,
  PlusCircle,
  useTooltip,
  ChevronRightIcon,
} from 'vvs-uikit'
import useGotchiOwner from 'state/vvsgotchi/hooks/useGotchiOwner'
import useToast from 'hooks/useToast'
import { toastTypes } from 'components/Toast'
import DashboardBox from 'views/VvsGotchi/components/DashboardBox'
import HealthMeter from 'views/VvsGotchi/components/HealthMeter'
import PixelatedImage from 'views/VvsGotchi/components/PixelatedImage'
import NoMoleModalListener from 'views/VvsGotchi/components/NoMoleModal'
import GachaponButton from 'views/VvsGotchi/Gachapon/Btn'
import { ExpeditionTeamRow } from '../components/ExpeditionTeamRow'
import { MAX_TEAM_SIZE } from '../config'
import { LightPageContainer } from '../styles'

import ExpBoost from './ExpBoost'
import HealthCareColumn from './HealthCareColumn'
import FeedAgainCountdown from './FeedAgainCountdown'
import DirtyDeclineCountdown from './DirtyDeclineCountdown'

import { TeamMainSection, DashboardNoticeFlex, HealthCareButton, HealthCareButtonTextsBox } from './styles'

const GotchiTeam: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const { team, owner } = useGotchiOwner()
  const { toastVVSGotchi } = useToast()

  const { targetRef, tooltip, tooltipVisible } = useTooltip(t('EXP boost based on team’s health'), {
    placement: 'top-start',
    tooltipOffset: [-13, 10],
  })

  const hasMoleInTeam = team && team.length > 0
  const isMaxHealth = Math.floor(owner?.teamHealthPoint) === 100
  const feedState = hasMoleInTeam
    ? isMaxHealth
      ? 'max-health'
      : team.some((m) => !m.hasFedToday)
      ? 'need-action'
      : 'done'
    : 'no-mole'
  const cleanState = hasMoleInTeam ? (owner.isClean ? 'done' : 'need-action') : 'no-mole'

  return (
    <LightPageContainer>
      <TeamMainSection>
        <RowBetween>
          <Flex alignItems="center">
            <Text color="darkestNightBlue" bold fontSize={20}>
              {t('Team')}
            </Text>
            <IconButton variant="text" onClick={() => history.push(Path.VVS_GOTCHI_TEAM_FAQ)}>
              <HelpWithQuestionmark width="28px" height="28px" />
            </IconButton>
          </Flex>
          <GachaponButton />
        </RowBetween>
        <PixelatedImage
          src="/images/vvsGotchi/mole_team.png"
          alt="mole team"
          mt={2}
          mb="-1px"
          width="200px"
          alignSelf="center"
          whenRetina
        />
        <DashboardBox mb={3}>
          <RowBetween mt={2}>
            <Text fontSize="13px" color="black">
              {t("Team's Health Meter")}
            </Text>
            <LinkButton
              fontSize="13px"
              fontWeight="700"
              onClick={() => history.push(Path.VVS_GOTCHI_TEAM_FAQ, { focus: 'teamHealth' })}
            >
              {t('What is this?')}
            </LinkButton>
          </RowBetween>

          <HealthMeter health={owner?.teamHealthPoint} />

          <Flex justifyContent="center" minHeight="5px" gap="4px">
            {typeof owner?.teamHealthPoint === 'number' && (
              <>
                {tooltipVisible && tooltip}
                <Box ref={targetRef}>
                  <Img src="/images/vvsGotchi/star.png" height="16px" />
                </Box>
                <ExpBoost expMultiplier={owner.expMultiplier} />
              </>
            )}
          </Flex>

          <Box mt={4}>
            {isMaxHealth && (
              <DashboardNoticeFlex
                type="info"
                onClick={() => history.push(Path.VVS_GOTCHI_TEAM_CONGRATS)}
                justifyContent="space-between"
              >
                <Flex gap="8px" alignItems="center">
                  <PixelatedImage src="/images/vvsGotchi/mysteryBox.png" width="29.27px" height="32px" />
                  <Flex flexDirection="column" ml={2} my={2}>
                    <Text fontSize="13px" bold color="darkestNightBlue">
                      {t('Congrats! You hit 100 health points!')}
                    </Text>
                    <Text fontSize="13px" color="darkestNightBlue">
                      {t('What does this mean for you?')}
                    </Text>
                  </Flex>
                </Flex>
                <ChevronRightIcon />
              </DashboardNoticeFlex>
            )}
            {!hasMoleInTeam && (
              <DashboardNoticeFlex type="info" onClick={() => history.push(Path.VVS_GOTCHI_TEAM_SELECT)}>
                <PlusCircle width="16px" />
                <Text fontSize="13px" fontWeight="700" color="darkestNightBlue">
                  {t('Add Miner Mole(s) to your Team to activate this Health Meter')}
                </Text>
              </DashboardNoticeFlex>
            )}
            {feedState === 'need-action' && (
              <DashboardNoticeFlex type="warn" onClick={() => history.push(Path.VVS_GOTCHI_TEAM_FEED)}>
                <Text fontSize="13px" fontWeight="700" color="darkestNightBlue">
                  {t('Feed to boost health')}
                </Text>
              </DashboardNoticeFlex>
            )}
            {cleanState === 'need-action' && (
              <DashboardNoticeFlex type="warn" onClick={() => history.push(Path.VVS_GOTCHI_TEAM_CLEAN)}>
                <Text fontSize="13px" fontWeight="700" color="darkestNightBlue">
                  {t('Mole is dirty! Clean it up before count down to prevent losing health!')}
                </Text>
              </DashboardNoticeFlex>
            )}
          </Box>
        </DashboardBox>

        <Grid gridTemplateColumns="repeat(2, 1fr)" mb={4} gridGap={3}>
          <HealthCareColumn
            title={
              <>
                <Text color="darkestNightBlue" bold fontSize={20}>
                  {t('Feeding')}
                </Text>
                <IconButton variant="text" onClick={() => history.push(Path.VVS_GOTCHI_TEAM_FAQ, { focus: 'feeding' })}>
                  <HelpWithQuestionmark width="28px" height="28px" />
                </IconButton>
              </>
            }
          >
            <HealthCareButton
              state={feedState}
              onClick={() => {
                if (feedState === 'done' || isMaxHealth) {
                  toastVVSGotchi(
                    isMaxHealth ? t('No Feeding needed! ( ＾0＾)') : t('Team is full! ( • ω •)'),
                    toastTypes.INFO,
                    <PixelatedImage src="/images/vvsGotchi/tableware.png" width="18px" whenRetina />,
                    isMaxHealth
                      ? t('Team is at full health! Come back when they are hungry again.')
                      : t('No feeding needed right now'),
                  )
                  return
                }
                history.push(Path.VVS_GOTCHI_TEAM_FEED)
              }}
            >
              <Flex alignSelf="stretch" justifyContent="center">
                <PixelatedImage
                  src={
                    feedState === 'no-mole' ? '/images/vvsGotchi/tableware_gray.png' : '/images/vvsGotchi/tableware.png'
                  }
                  height="26px"
                  whenRetina
                />
              </Flex>
              {feedState === 'need-action' ? (
                <HealthCareButtonTextsBox>
                  <Text color="black" fontWeight="600" mb={2}>
                    {t('Team is hungry')}
                  </Text>
                  <Text color="black" fontSize="13px">
                    {t('Feed now')}
                  </Text>
                </HealthCareButtonTextsBox>
              ) : feedState === 'max-health' ? (
                <HealthCareButtonTextsBox>
                  <Text color="black" fontWeight="600" mb={2}>
                    {t('Team is full!')}
                  </Text>
                  <Text color="black" fontSize="13px">
                    {t('No feeding needed!')}
                  </Text>
                </HealthCareButtonTextsBox>
              ) : feedState === 'done' ? (
                <HealthCareButtonTextsBox>
                  <Text color="black" fontWeight="600" mb={2}>
                    {t('Team is full')}
                  </Text>
                  <FeedAgainCountdown />
                </HealthCareButtonTextsBox>
              ) : (
                // feedState === 'no-mole'
                <Text textAlign="center" px={2} mx="auto" color="darkGrey" fontWeight="600">
                  {t('No Moles to Feed')}
                </Text>
              )}
            </HealthCareButton>
          </HealthCareColumn>
          <HealthCareColumn
            title={
              <>
                <Text color="darkestNightBlue" bold fontSize={20}>
                  {t('Cleaning')}
                </Text>
                <IconButton
                  variant="text"
                  onClick={() => history.push(Path.VVS_GOTCHI_TEAM_FAQ, { focus: 'cleaning' })}
                >
                  <HelpWithQuestionmark width="28px" height="28px" />
                </IconButton>
              </>
            }
          >
            <HealthCareButton
              state={cleanState}
              onClick={() => {
                if (cleanState === 'done') {
                  toastVVSGotchi(
                    t('Team is clean ( ＾0＾)'),
                    toastTypes.INFO,
                    <PixelatedImage src="/images/vvsGotchi/toilet.png" width="18px" whenRetina />,
                    t('No cleaning needed right now'),
                  )
                  return
                }
                history.push(Path.VVS_GOTCHI_TEAM_CLEAN)
              }}
            >
              <Flex alignSelf="stretch" justifyContent="center">
                <PixelatedImage
                  src={
                    cleanState === 'no-mole'
                      ? '/images/vvsGotchi/toilet_gray.png'
                      : cleanState === 'need-action'
                      ? '/images/vvsGotchi/poop.png'
                      : // cleanState === 'done'
                        '/images/vvsGotchi/toilet.png'
                  }
                  height="26px"
                  whenRetina
                />
              </Flex>
              {cleanState === 'done' ? (
                <HealthCareButtonTextsBox>
                  <Text color="black" fontWeight="600" mb={2}>
                    {t('Team is clean')}
                  </Text>
                  <Text color="black" fontSize="13px">
                    {t('Nothing to clean')}
                  </Text>
                </HealthCareButtonTextsBox>
              ) : cleanState === 'need-action' ? (
                <HealthCareButtonTextsBox>
                  <Text color="black" fontWeight="600" mb={2}>
                    {t('Team is dirty')}
                  </Text>
                  <DirtyDeclineCountdown time={owner.nextDirtyDeclineTime} />
                </HealthCareButtonTextsBox>
              ) : (
                // cleanState === 'no-mole'
                <Text textAlign="center" px={2} mx="auto" color="darkGrey" fontWeight="600">
                  {t('No Moles to clean')}
                </Text>
              )}
            </HealthCareButton>
          </HealthCareColumn>
        </Grid>

        <RowBetween mt={3}>
          <Text bold color="darkestNightBlue">
            {t('%selected% out of %max% added', {
              selected: team?.length ?? 0,
              max: MAX_TEAM_SIZE,
            })}
          </Text>
          <LinkButton color="black" underline="always" onClick={() => history.push(Path.VVS_GOTCHI_TEAM_SELECT)}>
            {t('Edit team')}
          </LinkButton>
        </RowBetween>
        <Box mt={3}>
          <ExpeditionTeamRow />
        </Box>
      </TeamMainSection>

      <NoMoleModalListener />
    </LightPageContainer>
  )
}

export default GotchiTeam
